import { useState, useEffect, useContext, useCallback, memo, type FC } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { Button } from "@foris/avocado-suite";
import { packagePopulationsQuery } from "@services/domains/packages";
import { PathParams } from "@edhtypes/general";
import { Population } from "@models/ISchema";
import { DataReducerType } from "../context/types";
import { Context } from "../context/PackagesContext";
import { Events, useListenEvent } from "@common/hooks/useListenEvent";
import { Types as DataTypes } from "../context/data.reducer";
import {
  PackageDetailsFields,
  type SelectedPopulationOption,
} from "../package-with-leagues-creation";
import css from "./packages.module.scss";

interface CreationFormProps {
  isLoading?: boolean;
  className?: string;
  showActionButtons?: boolean;
  onValidForm?: (isValid: boolean) => void;
  onSubmit: (dryRun: boolean, skipValidations: boolean, data?: DataReducerType) => void;
  onCancel?: () => void;
}

const CreationForm: FC<CreationFormProps> = ({
  isLoading = false,
  className,
  showActionButtons = true,
  onValidForm,
  onSubmit,
  onCancel,
}) => {
  // Hooks for handle the context and the client
  const client = useApolloClient();
  const { state, dispatch } = useContext(Context);
  const { origin, scenario }: PathParams = useParams();
  const { t } = useTranslation();
  // States for handle the form
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [selectedPopulation, setSelectedPopulation] = useState<SelectedPopulationOption>(null);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedCampus, setSelectedCampus] = useState("");
  const [selectedModality, setSelectedModality] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [packageName, setPackageName] = useState("");
  const [isMounted, setIsMounted] = useState(false);

  const handlePackageCreation = () => {
    dispatch({
      type: DataTypes.AddCreation,
      payload: {
        population: selectedPopulation?.self,
        termId: selectedTerm,
        linkId: state?.data?.link?.id || undefined,
        packageName,
      },
    });
  };

  /**
   * Reject the populations that have already been created
   *
   * @param populations - Population[]
   * @return Population[]
   */
  const selectablePopulations = (populations: Population[]): Population[] => {
    const createdPopulationCodes = Object.values(state?.data?.creations || {}).reduce(
      (acc: { [key: string]: boolean }, creation) => {
        if (creation?.population?.code) {
          acc[creation.population.code] = true;
        }
        return acc;
      },
      {},
    );

    return populations.filter(population => !createdPopulationCodes[population.code]);
  };

  /**
   * If a creation was added, submit a `dryRun` `packageCrud` request.
   */
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    }

    if (Object.keys(state?.data?.creations ?? {}).length > 0 && isMounted) {
      onSubmit(true, true, state?.data);
    }
  }, [state?.data?.creations]);

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      const size = 20;

      try {
        const queryParams = {
          query: packagePopulationsQuery,
          variables: {
            termId: selectedTerm,
            scenarioId: scenario,
            originId: origin,
            filter: {
              fields: {
                campusId: selectedCampus
                  ? {
                      eq: selectedCampus,
                    }
                  : undefined,
                modalityId: selectedModality
                  ? {
                      eq: selectedModality,
                    }
                  : undefined,
                programId: selectedProgram
                  ? {
                      eq: selectedProgram,
                    }
                  : undefined,
              },
              pagination: { page, size, searchTerm },
            },
          },
        };

        const { data } = await client.query(queryParams);
        const { pageInfo, items } = data?.cube?.complementedPopulations ?? {};

        const populationsToMap = selectablePopulations(items ?? []);
        const options: SelectedPopulationOption[] = populationsToMap.map((item: Population) => ({
          label: item?.code,
          value: item.id,
          index: item.packageIndex + 1,
          self: item,
        }));

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client, selectedTerm, selectedCampus, selectedModality, selectedProgram],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = searchTerm === newSearchTerm ? page + 1 : 1;

    setSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await requestItems(searchTerm, newSearchPage);
    dispatch({
      type: DataTypes.SetPopulations,
      payload: Object.values(options)?.map(option => option.self) ?? [],
    });

    return {
      options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  const resetPopulationOptions = () => {
    setSearchTerm("");
    setSelectedPopulation(null);
    dispatch({ type: DataTypes.SetPopulations, payload: [] });
  };

  const handleTermSelect = (term = "") => {
    resetPopulationOptions();
    setSelectedTerm(term);
  };

  const termKey = `${selectedTerm}${selectedCampus}${selectedModality}${selectedProgram}`;
  const isRequiredFieldsSelected = !!(
    selectedTerm &&
    (selectedCampus || selectedModality || selectedProgram)
  );
  const linkCampus = state?.data?.link?.course?.curriculum?.program?.campus;
  const isGlobalCampus = !!linkCampus?.isGlobal;

  useEffect(() => {
    setSelectedPopulation(null);
    setPage(0);
  }, [termKey]);

  useEffect(() => {
    onValidForm?.(!!selectedPopulation && !!packageName);

    return () => {
      onValidForm?.(false);
    };
  }, [selectedPopulation, packageName]);

  useListenEvent({
    eventKey: "on:submit",
    handler: (detail: Events["on:submit"]) => {
      if (detail?.target === "package-creation") {
        handlePackageCreation();
      }
    },
  });

  return (
    <section className={cx(css.creationForm, className)}>
      <div className={css.creationForm_content}>
        <PackageDetailsFields
          selectedCampus={selectedCampus}
          selectedModality={selectedModality}
          selectedPopulation={selectedPopulation}
          packageName={packageName}
          populationSelectKey={termKey}
          canSelectPopulation={isRequiredFieldsSelected}
          defaultTerm={state?.data?.link?.bundle?.term}
          isTermDisabled={!!state?.data?.link?.bundle?.term?.id}
          defaultCampus={!isGlobalCampus ? linkCampus : null}
          isCampusDisabled={!!linkCampus?.id && !isGlobalCampus}
          loadPopulationOptions={loadOptions}
          onTermChange={handleTermSelect}
          onCampusChange={setSelectedCampus}
          onModalityChange={setSelectedModality}
          onProgramChange={setSelectedProgram}
          onPopulationChange={setSelectedPopulation}
          onPopulationMenuClose={() => {
            if (!searchTerm) return;
            setSearchTerm("");
            setPage(0);
          }}
          onPackageNameChange={setPackageName}
        />
      </div>
      {showActionButtons && (
        <div className={css.creationForm_actionsButtons}>
          <Button
            className={cx(css.selector__button)}
            disabled={isLoading}
            variant="ghost"
            onClick={onCancel}
          >
            {t("create-package.btn-cancel")}
          </Button>
          <Button
            disabled={!(selectedPopulation && packageName) || isLoading}
            className={cx(css.selector__button)}
            onClick={handlePackageCreation}
          >
            {t("create-package.btn-create")}
          </Button>
        </div>
      )}
    </section>
  );
};

export default memo(CreationForm);
